import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Service to manage profile picture URL across the application
 * Handles cache-busting for images that use the same filename
 */
@Injectable({
  providedIn: 'root'
})
export class ProfilePictureService {
  private readonly defaultProfilePicture = 'https://fdi-files.s3.amazonaws.com/public/userAvatar.jpg';
  private profilePictureSubject = new BehaviorSubject<string>(this.defaultProfilePicture);

  /**
   * Observable stream of profile picture URL changes
   */
  profilePicture$: Observable<string> = this.profilePictureSubject.asObservable();

  /**
   * Get the current profile picture URL
   */
  getCurrentProfilePicture(): string {
    return this.profilePictureSubject.value;
  }

  /**
   * Set the profile picture URL with cache-busting
   * @param url - The base URL of the profile picture
   * @param bustCache - Whether to add cache-busting parameter (default: true)
   */
  setProfilePicture(url: string | null, bustCache: boolean = true): void {
    const baseUrl = url || this.defaultProfilePicture;

    // Don't add cache-buster to default image
    if (!bustCache || baseUrl === this.defaultProfilePicture) {
      this.profilePictureSubject.next(baseUrl);
      return;
    }

    // Add timestamp to bust browser cache
    const timestamp = new Date().getTime();
    const separator = baseUrl.includes('?') ? '&' : '?';
    const urlWithCacheBuster = `${baseUrl}${separator}t=${timestamp}`;

    this.profilePictureSubject.next(urlWithCacheBuster);
  }

  /**
   * Update profile picture after upload
   * Automatically adds cache-busting to force browser reload
   */
  updateAfterUpload(url: string): void {
    this.setProfilePicture(url, true);
  }

  /**
   * Initialize from auth token
   * Adds cache-busting on initial load to ensure fresh image
   */
  initializeFromToken(url: string | null): void {
    this.setProfilePicture(url, true);
  }
}
